/**
 * Balance Update Debugger
 * 
 * This utility helps debug balance updates and wallet connection events
 */

let isDebugMode = process.env.NODE_ENV === 'development';

export const balanceDebugger = {
    /**
     * Enable or disable balance update debugging
     */
    setDebugMode(enabled: boolean) {
        isDebugMode = enabled;
    },

    /**
     * Log SOL balance update
     */
    logSolUpdate(address: string, previous: number, current: number) {
        if (!isDebugMode) return;

        const timestamp = new Date().toLocaleTimeString();
        const change = current - previous;
        const sign = change >= 0 ? '+' : '';

        console.group(`%c🔄 SOL Balance Update [${timestamp}]`, 'color: #38cd9a; font-weight: bold');
        console.log(`Address: ${address.slice(0, 6)}...${address.slice(-4)}`);
        console.log(`Previous: ${previous.toFixed(6)} SOL`);
        console.log(`Current:  ${current.toFixed(6)} SOL`);
        console.log(`%cChange:   ${sign}${change.toFixed(6)} SOL`, change >= 0 ? 'color: green' : 'color: red');
        console.groupEnd();
    },

    /**
     * Log token balance update
     */
    logTokenUpdate(address: string, mint: string, symbol: string | null, previous: string, current: string) {
        if (!isDebugMode) return;

        const timestamp = new Date().toLocaleTimeString();
        const prevNum = parseFloat(previous) || 0;
        const currNum = parseFloat(current) || 0;
        const change = currNum - prevNum;
        const sign = change >= 0 ? '+' : '';
        const tokenName = symbol || mint.slice(0, 6) + '...' + mint.slice(-4);

        console.group(`%c🔄 Token Balance Update [${timestamp}]`, 'color: #38cd9a; font-weight: bold');
        console.log(`Address: ${address.slice(0, 6)}...${address.slice(-4)}`);
        console.log(`Token: ${tokenName}`);
        console.log(`Mint: ${mint.slice(0, 6)}...${mint.slice(-4)}`);
        console.log(`Previous: ${previous}`);
        console.log(`Current:  ${current}`);
        console.log(`%cChange:   ${sign}${change.toFixed(6)}`, change >= 0 ? 'color: green' : 'color: red');
        console.groupEnd();
    },

    /**
     * Log WebSocket message event
     */
    logWebSocketMessage(messageType: string, data: any) {
        if (!isDebugMode) return;

        const timestamp = new Date().toLocaleTimeString();
        console.group(`%c📨 WebSocket Message [${timestamp}]: ${messageType}`, 'color: #38cd9a;');
        console.log(data);
        console.groupEnd();
    },

    /**
     * Log wallet connection events
     */
    logWalletEvent(eventType: 'connect' | 'disconnect' | 'change', address: string | null, details?: any) {
        if (!isDebugMode) return;

        const timestamp = new Date().toLocaleTimeString();
        let emoji = '';
        let color = '';

        switch (eventType) {
            case 'connect':
                emoji = '🔌';
                color = 'color: #4CAF50; font-weight: bold';
                break;
            case 'disconnect':
                emoji = '🔒';
                color = 'color: #F44336; font-weight: bold';
                break;
            case 'change':
                emoji = '🔄';
                color = 'color: #2196F3; font-weight: bold';
                break;
        }

        console.group(`%c${emoji} Wallet ${eventType} [${timestamp}]`, color);
        if (address) {
            console.log(`Address: ${address.slice(0, 8)}...${address.slice(-8)}`);
        } else {
            console.log('No wallet address');
        }

        if (details) {
            console.log('Details:', details);
        }

        console.groupEnd();
    },

    /**
     * Log subscription events
     */
    logSubscriptionEvent(eventType: 'subscribe' | 'unsubscribe', address: string, success: boolean, details?: any) {
        if (!isDebugMode) return;

        const timestamp = new Date().toLocaleTimeString();
        const emoji = eventType === 'subscribe' ? '📥' : '📤';
        const color = success ? 'color: #4CAF50; font-weight: bold' : 'color: #F44336; font-weight: bold';

        console.group(`%c${emoji} ${eventType} ${success ? 'Success' : 'Failed'} [${timestamp}]`, color);
        console.log(`Address: ${address.slice(0, 8)}...${address.slice(-8)}`);

        if (details) {
            console.log('Details:', details);
        }

        console.groupEnd();
    }
};

// Turn on debugging in development mode
if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Balance update debugging enabled');
}
